/**
 * @file stopKeeping.ts
 * @description Unpins an artifact from desktop sync — "Stop keeping".
 * Consumers: the pin control on the form and record toolbars, the per-file
 * toggle in the attachments panel, and the settings page that cleans up pins
 * whose artifact is gone.
 *
 * It deletes **every** entry naming the path, not the first one it finds. That
 * is what makes unpin idempotent under settled decision 4 — the map is keyed by
 * pin id, so duplicates are representable — and it is why a duplicate pin is a
 * repairable state rather than a permanent one.
 *
 * Reads the current map the same way `keep` does: off the `users/{userId}`
 * store `UserProvider` keeps connected, and behind the same
 * `enqueueDesktopPinWrite` chain, so an unpin that overlaps a pin sees the pin.
 * Unpinning a path that is not pinned writes nothing unless the derived
 * `desktopPinPaths` has drifted, in which case the recomputation heals it.
 */

import type { User } from '#types';
import { createDocumentStore } from '../../store';
import { enqueueDesktopPinWrite } from './enqueueDesktopPinWrite';
import { resolveDesktopPinWrite } from './resolveDesktopPinWrite';

export const stopKeeping = async (userId: string, pinPath: string): Promise<void> =>
    enqueueDesktopPinWrite(userId, async () => {
        const store = createDocumentStore<User>(`users/${userId}`);
        const user = store.state.data;
        if (!user) {
            throw new Error(`[desktopSync] users/${userId} has not loaded; stopKeeping() cannot write a pin without the current map.`);
        }

        const update = resolveDesktopPinWrite({
            userId,
            pins: user.desktopPins,
            pinPaths: user.desktopPinPaths,
            pinPath,
            scope: null,
        });
        if (!update) return;

        await store.update(update, { debounce: false });
    });
